import Drawer from '@/components/Drawer';
import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import { getEmailTemplateRequest } from '@/services/api/emailCenter';
import { EmailTemplateModel } from '@/typings/api/emailCenter';
import { buildThemePreviewContext, isNl2goTemplate } from '@/utils/emailCenter';
import { F, getIntlTranslation } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Select, Skeleton, Space } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import {
  extractTransKeys,
  extractVariablePaths,
} from '../../components/EmailBodyEditor/insertion';
import {
  humanizeTransKey,
  humanizeVariablePath,
} from '../../components/EmailBodyEditor/labels';
import PreviewPane from '../../components/EmailBodyEditor/PreviewPane';
import SampleSourcePicker from '../../components/EmailBodyEditor/SampleSourcePicker';
import TestDataPanel from '../../components/EmailBodyEditor/TestDataPanel';
import {
  isCompleteHtmlDocument,
  renderTwigPreview,
} from '../../components/EmailBodyEditor/twigRenderer';
import { useAppliedSampleValues } from '../../components/EmailBodyEditor/useSampleData';
import { LANGUAGE_CODES } from '../../constants';
import { resolveTranslations } from '../../translationCatalog';
import { useJourneyTheme } from '../../useJourneyTheme';
import { useSaveTranslation } from '../../useSaveTranslation';

import styles from './TemplatePreviewDrawer.less';

/**
 * Standalone template preview: the same twig.js client-side rendering as the
 * editor's preview pane, plus sample values for the placeholders the template
 * actually uses, partner translations feeding its `{{ 'group.key' | trans }}`
 * filters, and a preview-language toggle that switches which language's
 * translation values apply.
 *
 * The sample values can also be filled from a **real** lead or appointment
 * ({@link SampleSourcePicker}), which is how someone checks the mail a specific
 * customer received rather than one assembled from invented values. Those values
 * land in the same fields and stay editable on top.
 *
 * None of that is offered for an NL2Go template ({@link isNl2goTemplate}): its
 * placeholders are named differently, so every one of those fields would sit
 * there filling in nothing. The render itself still shows — what the mail looks
 * like is still worth seeing — and the translations panel stays, since wording
 * is shared across providers.
 */
const TemplatePreviewDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // The partner's own list, not the product's full one — see
  // `selectTranslationLanguages`. Unset means every language.
  const languageOptions = useSelector(
    EmailCenterSelectors.selectTranslationLanguageOptions,
  );

  const drawer = useSelector(EmailCenterSelectors.selectDrawer('preview'));
  const translations = useSelector(EmailCenterSelectors.selectTranslations);
  const fragmentSources = useSelector(
    EmailCenterSelectors.selectFragmentSources,
  );
  const defaultLayoutKey = useSelector(
    EmailCenterSelectors.selectDefaultLayoutKey,
  );
  const { theme } = useJourneyTheme();

  const record = drawer.record as EmailTemplateModel | null;

  const [detail, setDetail] = useState<EmailTemplateModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState<string>(LANGUAGE_CODES[0]);
  const [testValues, setTestValues] = useState<Record<string, string>>({});
  // Filling from a record replaces the previous record's values and leaves
  // hand-typed ones alone; see `useAppliedSampleValues`.
  const { appliedSource, applyValues, clearValues, resetSource } =
    useAppliedSampleValues(setTestValues);
  // Bumped on every reset, purely to remount the record picker; see
  // `resetPreviewInput`.
  const [previewSession, setPreviewSession] = useState(0);
  // Local translation overrides, kept per language so toggling is lossless.
  const [transOverrides, setTransOverrides] = useState<
    Record<string, Record<string, string>>
  >({});
  const [savingTransKeys, setSavingTransKeys] = useState<
    Record<string, boolean>
  >({});

  /** Back to an unfilled preview — on close, and again on the next opening. */
  const resetPreviewInput = useCallback(() => {
    setTestValues({});
    resetSource(null);
    setTransOverrides({});
    // Remounts the record picker: its selection is its own state, and a picker
    // still showing "Anna Alt" over fields that were just reset would be a lie.
    setPreviewSession((session) => session + 1);
  }, [resetSource]);

  useEffect(() => {
    if (!drawer.visible || !record) {
      // Closing clears the fill rather than the next opening doing it, so a
      // reopened preview never paints the last customer's values first.
      setDetail(null);
      resetPreviewInput();
      return undefined;
    }
    resetPreviewInput();
    // Translations + fragments are partner-wide reference data; the model
    // dedupes refetches, so dispatching per opening is cheap.
    dispatch({ type: EMAIL_CENTER_ACTIONS.FETCH_TRANSLATIONS });
    dispatch({ type: EMAIL_CENTER_ACTIONS.FETCH_FRAGMENTS });
    dispatch({ type: EMAIL_CENTER_ACTIONS.FETCH_EMAIL_SETTINGS });

    let cancelled = false;
    setLoading(true);
    getEmailTemplateRequest({ params: { uuid: record.id } })
      .then((result) => {
        if (cancelled) return;
        processAPIResponseNow(
          result,
          {
            onSuccess: (fetched: EmailTemplateModel | undefined) => {
              setDetail(fetched && fetched.body != null ? fetched : record);
            },
            // The row record is the fallback so the preview still opens when
            // the detail request fails — degrade silently instead of toasting.
            onError: () => setDetail(record),
          },
          'data',
        );
      })
      .catch(() => {
        if (!cancelled) setDetail(record);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.visible, record?.id]);

  const close = useCallback(() => {
    dispatch({
      type: EMAIL_CENTER_ACTIONS.CLOSE_DRAWER,
      payload: { drawer: 'preview' },
    });
  }, [dispatch]);

  const template = detail ?? record;
  // NL2Go bodies use their own placeholder names, so nothing we could offer to
  // fill would land. The provider is being retired rather than supported.
  const variablesFillable = !isNl2goTemplate(template);
  const fullSource = `${template?.subject ?? ''}\n${template?.pretext ?? ''}\n${
    template?.body ?? ''
  }`;

  const transKeys = useMemo(() => extractTransKeys(fullSource), [fullSource]);

  // Partner translations for the chosen language, `group.key` → value.
  // Empty strings are kept: an existing-but-empty translation resolves to ''
  // on the server (hiding optional blocks) — only absent keys are "missing".
  const partnerTransMap = useMemo(
    () => resolveTranslations(translations, language),
    [translations, language],
  );

  const themeContext = useMemo(() => buildThemePreviewContext(theme), [theme]);

  // Baseline render without sample values: whatever `{{ … }}` placeholders
  // and `{% if %}` condition variables survive it (body variables, provider
  // merge tags contributed by fragments, merge conditionals) is exactly the
  // set the user can fill — a stable row list that doesn't shrink as values
  // are typed.
  // Mirror the server's layout resolution: the template's own layout wins;
  // otherwise the partner default wraps the body — unless the body is
  // already a complete HTML document (imported marketing emails), which an
  // {% extends %} would erase.
  const effectiveLayoutKey =
    template?.layout_key ||
    (isCompleteHtmlDocument(template?.body ?? '') ? null : defaultLayoutKey) ||
    null;

  const { placeholderPaths, conditionPaths } = useMemo(() => {
    const baseline = renderTwigPreview(template?.body ?? '', {
      context: { ...themeContext, language },
      translations: partnerTransMap,
      fragments: fragmentSources,
      layoutKey: effectiveLayoutKey,
    });
    const placeholders = extractVariablePaths(fullSource);
    for (const path of baseline.placeholderVariables) {
      if (!placeholders.includes(path)) placeholders.push(path);
    }
    return {
      placeholderPaths: placeholders,
      conditionPaths: baseline.conditionVariables.filter(
        (path) => !placeholders.includes(path),
      ),
    };
  }, [
    template?.body,
    effectiveLayoutKey,
    fullSource,
    themeContext,
    language,
    partnerTransMap,
    fragmentSources,
  ]);

  const mergedVarContext = useMemo(() => {
    const merged: Record<string, string> = {
      ...themeContext,
      language,
    };
    for (const [path, sample] of Object.entries(testValues)) {
      if (sample !== '') merged[path] = sample;
    }
    return merged;
  }, [themeContext, language, testValues]);

  const mergedTransMap = useMemo(
    () => ({ ...partnerTransMap, ...(transOverrides[language] ?? {}) }),
    [partnerTransMap, transOverrides, language],
  );

  const render = useCallback(
    (source: string) =>
      renderTwigPreview(source, {
        context: mergedVarContext,
        translations: mergedTransMap,
        fragments: fragmentSources,
      }).html,
    [mergedTransMap, mergedVarContext, fragmentSources],
  );

  const renderedSubject = render(template?.subject ?? '');
  const renderedPretext = template?.pretext ? render(template.pretext) : '';

  const handleTestValueChange = useCallback((path: string, sample: string) => {
    setTestValues((current) => ({ ...current, [path]: sample }));
  }, []);

  /** Row extras that say where a value came from; see `TestDataPanel`. */
  const provenanceOf = useCallback(
    (path: string) => ({
      sourceLabel: appliedSource?.label,
      sourceValue: appliedSource?.values[path],
    }),
    [appliedSource],
  );

  const handleTransChange = useCallback(
    (key: string, value: string) => {
      setTransOverrides((current) => ({
        ...current,
        [language]: { ...(current[language] ?? {}), [key]: value },
      }));
    },
    [language],
  );

  // Persists the edited value as the partner's translation for the previewed
  // language; on success the override is dropped so the refreshed catalog
  // value (now identical) takes over as the resolved placeholder.
  const saveTranslation = useSaveTranslation();
  const handleTransSave = useCallback(
    (key: string, value: string) => {
      setSavingTransKeys((current) => ({ ...current, [key]: true }));
      saveTranslation(key, { [language]: value }).then((saved) => {
        setSavingTransKeys((current) => ({ ...current, [key]: false }));
        // `useSaveTranslation` has already reported the reason — a generic
        // "could not save" on top of it would only bury the server's.
        if (!saved) return;
        // The refreshed catalog now carries this wording, so the local override
        // would only shadow it.
        setTransOverrides((current) => {
          const forLanguage = { ...(current[language] ?? {}) };
          delete forLanguage[key];
          return { ...current, [language]: forLanguage };
        });
      });
    },
    [saveTranslation, language],
  );

  return (
    <Drawer
      width={980}
      open={drawer.visible}
      onOpenChange={close}
      title={
        <Space className={styles.header} size={12}>
          <span>{record?.name}</span>
          <Select
            size="small"
            className={styles.languageSelect}
            value={language}
            onChange={setLanguage}
            options={languageOptions}
            title={getIntlTranslation(intl, {
              id: 'pages.emailCenter.preview.language',
              defaultMessage: 'Preview language',
            })}
          />
        </Space>
      }
    >
      {loading || !template ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <div className={styles.previewLayout}>
          <div className={styles.sampleColumn}>
            {variablesFillable && (
              <SampleSourcePicker
                key={previewSession}
                language={language}
                onApply={applyValues}
                onClear={clearValues}
              />
            )}
            <TestDataPanel
              appliedSourceLabel={appliedSource?.label}
              sections={[
                // The variable fields are the NL2Go cut: what fills placeholders
                // and what decides which optional parts show, both keyed on our
                // paths. The wording panel below is provider-independent.
                ...(!variablesFillable
                  ? []
                  : [
                      {
                        title: (
                          <F
                            id="pages.emailCenter.editor.testData"
                            defaultMessage="Sample data"
                          />
                        ),
                        hint: (
                          <F
                            id="pages.emailCenter.editor.testDataHint"
                            defaultMessage="Example values for this preview only — nothing is saved."
                          />
                        ),
                        rows: placeholderPaths.map((path) => ({
                          key: path,
                          ...humanizeVariablePath(path),
                          value: testValues[path] ?? '',
                          placeholder: themeContext[path],
                          ...provenanceOf(path),
                        })),
                        onChange: handleTestValueChange,
                        onRestore: handleTestValueChange,
                      },
                      {
                        title: (
                          <F
                            id="pages.emailCenter.editor.conditions"
                            defaultMessage="Optional content"
                          />
                        ),
                        hint: (
                          <F
                            id="pages.emailCenter.editor.conditionsHint"
                            defaultMessage="These values decide which parts of the email show. Enter a value to try a part — 'false' or '0' hides it."
                          />
                        ),
                        rows: conditionPaths.map((path) => ({
                          key: path,
                          ...humanizeVariablePath(path),
                          value: testValues[path] ?? '',
                          placeholder: themeContext[path],
                          ...provenanceOf(path),
                        })),
                        onChange: handleTestValueChange,
                        onRestore: handleTestValueChange,
                      },
                    ]),
                {
                  title: (
                    <F
                      id="pages.emailCenter.preview.translations"
                      defaultMessage="Email texts"
                    />
                  ),
                  hint: (
                    <F
                      id="pages.emailCenter.preview.translationsHint"
                      defaultMessage="Wording shared across your emails. Open one to edit it in every language."
                    />
                  ),
                  rows: transKeys.map((key) => ({
                    key,
                    ...humanizeTransKey(key),
                    value: transOverrides[language]?.[key] ?? '',
                    placeholder: partnerTransMap[key],
                    missing: partnerTransMap[key] === undefined,
                    saveable: !!transOverrides[language]?.[key],
                    saving: !!savingTransKeys[key],
                  })),
                  onChange: handleTransChange,
                  onSave: handleTransSave,
                  // Renders the shared translation field: saving a wording here
                  // changes every email using the key, so it must not look like a
                  // per-preview sample box.
                  translationKeys: true,
                  language,
                  // Translations are reference data, not per-preview sample
                  // input — start collapsed unless a key is missing for the
                  // previewed language (a gap the user should see).
                  defaultCollapsed: transKeys.every(
                    (key) => partnerTransMap[key] !== undefined,
                  ),
                },
              ]}
            />
          </div>
          <div className={styles.previewColumn}>
            <div className={styles.subjectRow}>
              <span className={styles.subjectLabel}>
                <F
                  id="pages.emailCenter.templates.subject"
                  defaultMessage="Subject"
                />
              </span>
              <span className={styles.subjectValue}>{renderedSubject}</span>
            </div>
            {template.pretext && (
              <div className={styles.pretext}>{renderedPretext}</div>
            )}
            <PreviewPane
              html={template.body ?? ''}
              previewContext={mergedVarContext}
              translations={mergedTransMap}
              fragments={fragmentSources}
              layoutKey={effectiveLayoutKey}
              subjectText={renderedSubject}
              pretextText={renderedPretext}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default TemplatePreviewDrawer;
